// ============================================================
// ✅ ReglesTab.jsx - Gestion des règles (CRUD + Actif/Inactif)
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Plus, Trash2, Edit, Save, X, RefreshCw } from 'lucide-react';

export default function ReglesTab() {
  const [regles, setRegles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ Code: '', Libelle: '', Valeur: '', TypeValeur: 'NUMBER', Categorie: 'DUREE' });

  useEffect(() => { loadRegles(); }, []);

  const loadRegles = async () => {
    setLoading(true);
    try { const res = await api.get('/regles'); setRegles(res.data.regles || []); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleEdit = (r) => { setEditingId(r.ID_Regle); setEditValue(r.Valeur); };
  const handleSave = async (id) => {
    try { await api.put(`/regles/${id}`, { Valeur: editValue }); setEditingId(null); loadRegles(); }
    catch (err) { alert('Erreur'); }
  };
  const handleDelete = async (r) => {
    if (r.NombreLiaisons > 0) { alert(`Règle liée à ${r.NombreLiaisons} type(s) de congé.`); return; }
    if (!window.confirm('Supprimer ?')) return;
    try { await api.delete(`/regles/${r.ID_Regle}`); loadRegles(); }
    catch (err) { alert(err.response?.data?.message || 'Erreur'); }
  };
  const handleToggle = async (r) => {
    try { await api.put(`/regles/${r.ID_Regle}`, { Valeur: r.Valeur, EstActif: r.EstActif ? 0 : 1 }); loadRegles(); }
    catch (err) { alert('Erreur'); }
  };
  const handleAdd = async () => {
    if (!form.Code || !form.Valeur) { alert('Code et Valeur requis'); return; }
    try { await api.post('/regles', form); setShowModal(false); setForm({ Code: '', Libelle: '', Valeur: '', TypeValeur: 'NUMBER', Categorie: 'DUREE' }); loadRegles(); }
    catch (err) { alert(err.response?.data?.message || 'Erreur'); }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Chargement...</div>;

  return (
    <>
      <div className="flex justify-between mb-3">
        <p className="text-xs text-gray-500">{regles.length} règle(s)</p>
        <button onClick={() => setShowModal(true)} className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs flex items-center gap-1"><Plus size={12} /> Ajouter</button>
        <button onClick={loadRegles} className="p-2 text-gray-400 hover:text-emerald-600"><RefreshCw size={16} /></button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50"><tr className="text-[11px] text-gray-500">
              <th className="px-3 py-2 text-left">Code</th><th className="px-3 py-2 text-left">Libellé</th>
              <th className="px-3 py-2 text-center">Valeur</th><th className="px-3 py-2 text-center">Catégorie</th>
              <th className="px-3 py-2 text-center">Liaisons</th><th className="px-3 py-2 text-center">Actif</th>
              <th className="px-3 py-2 text-center">Actions</th>
            </tr></thead>
            <tbody className="divide-y">
              {regles.map(r => (
                <tr key={r.ID_Regle} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-[11px]">{r.Code}</td>
                  <td className="px-3 py-2">{r.Libelle}</td>
                  <td className="px-3 py-2 text-center">
                    {editingId === r.ID_Regle ? (
                      <div className="flex items-center gap-1 justify-center">
                        <input value={editValue} onChange={e => setEditValue(e.target.value)} className="w-16 p-1 border rounded text-xs text-center" />
                        <button onClick={() => handleSave(r.ID_Regle)} className="p-1 text-green-500"><Save size={12} /></button>
                        <button onClick={() => setEditingId(null)} className="p-1 text-gray-400"><X size={12} /></button>
                      </div>
                    ) : <span className="font-bold">{r.Valeur}</span>}
                  </td>
                  <td className="px-3 py-2 text-center"><span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{r.Categorie}</span></td>
                  <td className="px-3 py-2 text-center">{r.NombreLiaisons || 0}</td>
                  <td className="px-3 py-2 text-center cursor-pointer" onClick={() => handleToggle(r)}>{r.EstActif ? '✅' : '❌'}</td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleEdit(r)} className="p-1 text-blue-400 hover:text-blue-600"><Edit size={14} /></button>
                      <button onClick={() => handleDelete(r)} className="p-1 text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">➕ Nouvelle règle</h3>
            <div className="space-y-3">
              <input className="w-full p-2 border rounded-lg text-sm" placeholder="Code" value={form.Code} onChange={e => setForm({...form, Code: e.target.value.toUpperCase()})} />
              <input className="w-full p-2 border rounded-lg text-sm" placeholder="Libellé" value={form.Libelle} onChange={e => setForm({...form, Libelle: e.target.value})} />
              <div className="flex gap-2">
                <input className="flex-1 p-2 border rounded-lg text-sm" placeholder="Valeur" value={form.Valeur} onChange={e => setForm({...form, Valeur: e.target.value})} />
                <select className="w-24 p-2 border rounded-lg text-sm" value={form.TypeValeur} onChange={e => setForm({...form, TypeValeur: e.target.value})}>
                  <option value="NUMBER">Nombre</option><option value="BOOLEAN">Booléen</option><option value="STRING">Texte</option>
                </select>
              </div>
              <select className="w-full p-2 border rounded-lg text-sm" value={form.Categorie} onChange={e => setForm({...form, Categorie: e.target.value})}>
                <option value="DUREE">Durée</option><option value="CALCUL">Calcul</option><option value="FERIES">Fériés</option><option value="FRACTIONNEMENT">Fractionnement</option><option value="WEEKEND">Week-end</option>
              </select>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 bg-gray-100 rounded-lg text-sm">Annuler</button>
                <button onClick={handleAdd} className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm">Ajouter</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}